import {isEqual} from 'lodash';
import mapKeysToCase from '@/utils/mapKeysToCase';
import type {
  SegmentFilters,
  SegmentHandleFilter,
} from '@/types/models/segments/segment';
import {SEGMENT_STATE, type SegmentState} from '@/types/segmentState';

/**
 * Recursive type for values that appear inside segment filters (profile, platforms,
 * locations, etc.). Used so comparison helpers stay typed instead of unknown.
 */
type FilterTreeValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | FilterTreeValue[]
  | {[key: string]: FilterTreeValue};

/** Canonical list of platform ids (excluding 'all'); matches search-crm mapToHandleFilter. */
const ALL_PLATFORMS_LIST = [
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
  'twitter',
  'pinterest',
] as const;

/**
 * Normalizes HandleFilter platforms for comparison: undefined, [], or the full
 * "all platforms" list are treated as equivalent (canonical undefined). Specific
 * platform lists are returned sorted for stable comparison.
 */
function normalizeHandlePlatforms(
  platforms: SegmentHandleFilter['platforms'] | null | undefined
): string[] | undefined {
  if (platforms === undefined || platforms === null) {
    return undefined;
  }
  if (platforms.length === 0) {
    return undefined;
  }
  const sorted = [...platforms].sort();
  const allSorted = [...ALL_PLATFORMS_LIST].sort();
  if (
    sorted.length === allSorted.length &&
    sorted.every((p, i) => p === allSorted[i])
  ) {
    return undefined;
  }
  return sorted;
}

/**
 * True when HandleFilter platforms means "all platforms" (undefined, empty, or
 * the full canonical list). Used when applying a handle segment so we set
 * platform to 'all' instead of the first element.
 */
export function isHandleFilterAllPlatforms(
  platforms: SegmentHandleFilter['platforms'] | null | undefined
): boolean {
  return normalizeHandlePlatforms(platforms) === undefined;
}

/**
 * Returns a deterministic string for a value so arrays can be sorted
 * order-independently (e.g. profile.locations).
 */
function stableSortKey(val: FilterTreeValue): string {
  if (val === null || val === undefined || typeof val !== 'object') {
    return JSON.stringify(val);
  }
  if (Array.isArray(val)) {
    return val.map(stableSortKey).join('|');
  }
  const record = val as Record<string, FilterTreeValue>;
  const keys = Object.keys(record).sort();
  return keys.map((k) => `${k}:${stableSortKey(record[k])}`).join(',');
}

/**
 * Normalizes filter object for comparison: strips undefined, treats empty
 * objects and empty arrays as absent, so that API segment shape and
 * convertFiltersToSegment output can match (e.g. omitted key vs undefined vs {}
 * vs []). Arrays are sorted by stableSortKey so order does not affect equality
 * (e.g. profile.locations).
 */
function normalizeForComparison(
  obj: FilterTreeValue
): FilterTreeValue | undefined {
  if (obj === undefined || obj === null) {
    return undefined;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    const arr = obj.map(normalizeForComparison).filter((x) => x !== undefined);
    if (arr.length === 0) return undefined;
    const sorted = [...arr].sort((a, b) =>
      stableSortKey(a).localeCompare(stableSortKey(b))
    );
    return sorted as FilterTreeValue;
  }
  const record = obj as Record<string, FilterTreeValue>;
  const result = Object.entries(record).reduce<Record<string, FilterTreeValue>>(
    (acc, [k, v]) => {
      const n = normalizeForComparison(v);
      if (n !== undefined) acc[k] = n;
      return acc;
    },
    {}
  );
  return Object.keys(result).length ? result : undefined;
}

/**
 * Returns true if segmentFilters is a subset of currentFilters (every key present
 * in segmentFilters exists in currentFilters with equal value; nested objects compared recursively).
 */
function isFilterSubset(
  currentFilters: FilterTreeValue,
  segmentFilters: FilterTreeValue
): boolean {
  if (segmentFilters === null || segmentFilters === undefined) {
    return true;
  }
  if (currentFilters === null || currentFilters === undefined) {
    return false;
  }
  if (typeof segmentFilters !== 'object') {
    return isEqual(currentFilters, segmentFilters);
  }
  if (Array.isArray(segmentFilters)) {
    if (!Array.isArray(currentFilters)) {
      return false;
    }
    if (segmentFilters.length !== currentFilters.length) {
      return false;
    }
    return segmentFilters.every((item, i) =>
      isFilterSubset(currentFilters[i], item)
    );
  }
  if (
    typeof segmentFilters === 'object' &&
    segmentFilters !== null &&
    !Array.isArray(currentFilters)
  ) {
    if (typeof currentFilters !== 'object' || currentFilters === null) {
      return false;
    }
    const segmentRecord = segmentFilters as Record<string, FilterTreeValue>;
    const currentRecord = currentFilters as Record<string, FilterTreeValue>;
    return Object.keys(segmentRecord).every((key) =>
      isFilterSubset(currentRecord[key], segmentRecord[key])
    );
  }
  return isEqual(currentFilters, segmentFilters);
}

/**
 * Compares current CRM filters (in segment format) with a segment's saved filters.
 * - ACTIVE: exact match (current filters equal segment filters).
 * - CONTAINED: segment filters are a subset of current (e.g. user added more filters).
 * - INACTIVE: no match or current is stricter than segment.
 */
export function compareFiltersWithSegment(
  currentFilters: SegmentFilters,
  segmentFilters: SegmentFilters
): SegmentState {
  if (currentFilters.type !== segmentFilters.type) {
    return SEGMENT_STATE.INACTIVE;
  }
  if (
    currentFilters.type === 'HandleFilter' &&
    segmentFilters.type === 'HandleFilter'
  ) {
    if (
      currentFilters.handle === segmentFilters.handle &&
      isEqual(
        normalizeHandlePlatforms(currentFilters.platforms),
        normalizeHandlePlatforms(segmentFilters.platforms)
      )
    ) {
      return SEGMENT_STATE.ACTIVE;
    }
    return SEGMENT_STATE.INACTIVE;
  }
  if (
    currentFilters.type === 'MultiPlatformFilter' &&
    segmentFilters.type === 'MultiPlatformFilter'
  ) {
    // API may return segment filters in snake_case (from_age, to_age, engagement_rate);
    // currentFilters from convertFiltersToSegment is always camelCase. Normalize so they match.
    const segmentFiltersCamel = mapKeysToCase(
      segmentFilters as object
    ) as SegmentFilters;
    // SegmentFilters has nested types with prototype methods; for recursive comparison we treat as tree-shaped data.
    const currentAsTree = currentFilters as unknown as FilterTreeValue;
    const segmentAsTree = segmentFiltersCamel as unknown as FilterTreeValue;
    if (
      isEqual(
        normalizeForComparison(currentAsTree),
        normalizeForComparison(segmentAsTree)
      )
    ) {
      return SEGMENT_STATE.ACTIVE;
    }
    if (isFilterSubset(currentAsTree, segmentAsTree)) {
      return SEGMENT_STATE.CONTAINED;
    }
    return SEGMENT_STATE.INACTIVE;
  }
  return SEGMENT_STATE.INACTIVE;
}
